import {
	type ChangeEvent,
	type FunctionComponent,
	type HTMLAttributes,
	type SetStateAction,
	useCallback,
	useEffect,
	useState,
	useContext
} from 'react';
import { type PartialPick } from '../../../../../@types/build';
import { HOW_TO_MEASURE_UOM_LINK } from '../../../constants/links';
import { AddToCartContext } from '../../../contexts/add-to-cart/add-to-cart.context';
import { formatPrice } from '../../../helpers/general-helper/general-helper';
import { ADD_TO_CART_BUTTON_TYPE } from '../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { calculatePackages, formatQuantity } from '../../../helpers/product-helper/sqft-calculator.helper';
import { useAddToCartButton } from '../../../hooks/add-to-cart/add-to-cart-button.hooks';
import { type ProductPriceInfo } from '../../../types/product.types';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { TextInput } from '../../inputs';
import { AddToCartButtonFactory } from '../add-to-cart-button/add-to-cart-button.component';
import { arrow, arrowContainer, arrowLeft } from './sqft-add-to-cart.css';

export type SqFtAddToCartProps = {
	priceInfo: PartialPick<ProductPriceInfo, 'packaging' | 'unitPrice'>;
	units?: SetStateAction<number>;
	setQuantity?: (quantity?: number) => void;
	pageName: string;
	onClick: () => void;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

type SqFtAddToCartState = {
	units: string;
	quantity: number;
};

export const SqFtAddToCart: FunctionComponent<SqFtAddToCartProps> = ({
	className = '',
	priceInfo,
	units,
	onClick,
	pageName,
	setQuantity
}) => {
	const DEFAULT_CLASSES = 'flex flex-column items-start ba b--theme-grey-light pa3';
	const DEFAULT_BOX_CLASSES = 'lh-title bg-theme-white ba b--theme-grey-light ph3 input';
	const DEFAULT_MESSAGE = 'Enter square footage';
	const packaging = priceInfo.packaging;
	const { setCartQuantity } = useContext(AddToCartContext);
	const [state, setState] = useState<SqFtAddToCartState>({
		units: packaging ? packaging.value.toFixed(2) : '',
		quantity: packaging ? calculatePackages({ total: parseFloat(packaging.value.toFixed(2)) }, packaging) : 1
	});

	const configurationValid = state.quantity > 0;

	const { buttonType, addToCartActionStatus } = useAddToCartButton({ pageName });

	/**
	 * Calculates next state
	 *
	 * @param {SqFtAddToCartState} newState
	 */
	const nextState = useCallback(
		(newState: SqFtAddToCartState): void => {
			const newQuantity = calculatePackages({ total: parseFloat(newState.units) }, packaging);

			setState({ ...newState, quantity: newQuantity });

			if (setQuantity) {
				setQuantity(newQuantity);
				setCartQuantity(newQuantity);
			}
		},

		[packaging, setQuantity]
	);
	/**
	 * State Change
	 */
	useEffect(() => {
		if (units) {
			nextState({ ...state, units: Number(units).toFixed(2) });
		}

		// if state or nextState is including in the dependency array, there is a infinite re-render.
	}, [units]);

	if (!priceInfo.packaging) {
		return null;
	}

	/**
	 * Quantity Input Change Handler
	 *
	 * @param {ChangeEvent} e
	 */
	const onQuantityChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const value = e.currentTarget.value;
		nextState({ ...state, units: value.length > 0 ? value : '' });
	};

	return (
		<section className={`${DEFAULT_CLASSES} ${className}`}>
			<header className={`flex flex-row items-center justify-between mv0 w-100`}>
				<h2 className={`mv0 f5`}>Square Footage</h2>
				<StyledLink url={HOW_TO_MEASURE_UOM_LINK}>How To Measure</StyledLink>
			</header>
			<div className={`flex flex-row items-start justify-between pv3 ph0 mt3`}>
				<div className={`w4`}>
					<TextInput
						ariaLabel={`value in ${packaging?.units}`}
						inputMode={`decimal`}
						maxLength={8}
						max={`8`}
						value={state.units}
						onChange={onQuantityChange}
						automationHook={'square-foot'}
					/>
				</div>
				<div className={`flex flex-row items-baseline items-center-ns ml3 w-100`}>
					<div className={`${arrowContainer}`} style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
						<span className={`db ${arrow} ${arrowLeft}`} />
					</div>
					<div className={`${DEFAULT_BOX_CLASSES}`}>
						{formatQuantity(state.quantity, packaging, DEFAULT_MESSAGE)}
						{state.quantity > 0 ? (
							<span className={`theme-emphasis ml2`}>{formatPrice(state.quantity * priceInfo.current)}</span>
						) : null}
					</div>
				</div>
			</div>
			<AddToCartButtonFactory
				buttonType={buttonType}
				addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.DEFAULT}
				handleOnClick={onClick}
				disabled={!configurationValid}
				status={addToCartActionStatus}
			/>
		</section>
	);
};
